import {
  normalizeTaxType,
  resolveLineItemTaxRate,
  getApplicationItemName,
  isMigrationApplicationType,
} from "./invoiceLineItemHelpers";
import { resolveInvoiceLineItemsFromRecord } from "./invoiceEditHelpers";

export function getClientApplicationId(application = {}) {
  return (
    application.application_id ??
    application.applicationId ??
    application.app_id ??
    application.appid ??
    application.id ??
    null
  );
}

export function isMigrationClientApplication(application = {}) {
  return isMigrationApplicationType(
    application.application_type ||
      application.applicationType ||
      application.type ||
      application.category,
  );
}

export function normalizeClientAllApplications(response) {
  const payload = response?.data;

  if (payload?.success && Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (payload?.status && Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
}

function formatApplicationMoney(value) {
  const amount = Number(value) || 0;
  return amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function resolveFinanceInvoiceLineTotal(row = {}) {
  const lineTotal = Number(row.line_total ?? row.amount) || 0;
  if (lineTotal > 0) return lineTotal;

  const qty = Number(row.quantity ?? row.qty) || 1;
  const unitPrice = Number(row.unit_price ?? row.unitPrice) || 0;
  const discount = Number(row.discount) || 0;

  return Math.max(0, qty * unitPrice - discount);
}

function roundMoney(value) {
  return Math.round(Number(value) * 100) / 100;
}

/** Sum invoiced line totals for a fee type on finance invoices (includes draft). */
export function resolveApplicationFeeInvoicedFromFinanceInvoices(
  invoices = [],
  applicationId,
  feeType = "",
  { excludeInvoiceId = null } = {},
) {
  if (!applicationId || !feeType) return 0;

  let invoicedTotal = 0;

  (Array.isArray(invoices) ? invoices : []).forEach((invoice) => {
    const invoiceApplicationId =
      invoice.application_id ?? invoice.applicationId ?? null;
    if (String(invoiceApplicationId) !== String(applicationId)) return;

    const invoiceId = invoice.id ?? invoice.financeInvoiceId ?? null;
    if (
      excludeInvoiceId &&
      invoiceId != null &&
      String(invoiceId) === String(excludeInvoiceId)
    ) {
      return;
    }

    const status = String(invoice.status || "").toLowerCase();
    if (status === "cancelled" || status === "void" || status === "refunded") {
      return;
    }

    const items = resolveInvoiceLineItemsFromRecord(invoice);
    items.forEach((item) => {
      const itemFeeType = String(item.fee_type ?? item.feeType ?? "").toLowerCase();
      if (itemFeeType !== String(feeType).toLowerCase()) return;

      const lineTotal = resolveFinanceInvoiceLineTotal(item);
      if (lineTotal <= 0) return;

      invoicedTotal += lineTotal;
    });
  });

  return roundMoney(invoicedTotal);
}

function resolveFeeRemainingAmount(fee = {}, paidAmount = 0) {
  const unitPrice = resolveFeeUnitPrice(
    fee,
    normalizeTaxType(fee.gst_status) || "notax",
  );
  return Math.max(0, roundMoney(unitPrice - (Number(paidAmount) || 0)));
}

function resolveFeeUnitPrice(fee = {}, taxType = "") {
  const cost = Number(fee.cost) || 0;
  const baseCost = Number(fee.base_cost) || 0;
  const effectiveTaxType = normalizeTaxType(taxType || fee.gst_status);

  if (effectiveTaxType === "inclusive") {
    return cost > 0 ? cost : baseCost;
  }

  if (effectiveTaxType === "exclusive") {
    return baseCost > 0 ? baseCost : cost;
  }

  return cost || baseCost;
}

function getApplicationTypeLabel(application = {}) {
  return isMigrationClientApplication(application) ? "Migration" : "Education";
}

function getClientApplicationName(application = {}) {
  const packageId = application.package_id ?? application.packageId;
  const courseId = application.course_id ?? application.courseid;

  if (
    packageId != null ||
    courseId === 0 ||
    courseId === "0" ||
    application.package_name ||
    application.packagename
  ) {
    return (
      application.application ||
      application.package_name ||
      application.packagename ||
      application.course_name ||
      application.coursename ||
      application.name ||
      ""
    );
  }

  return (
    application.application ||
    application.course_name ||
    application.coursename ||
    application.package_name ||
    application.packagename ||
    application.visa_name ||
    application.visaname ||
    application.name ||
    getApplicationItemName(application) ||
    ""
  );
}

function formatFeeAmount(fee = {}, fallbackCurrency = "AUD") {
  const currency = fee.currency || fallbackCurrency || "AUD";
  const amount = Number(fee.cost || fee.base_cost) || 0;
  return `${currency} ${formatApplicationMoney(amount)}`;
}

function buildApplicationFeeLabel(application = {}, fee = {}) {
  const typeLabel = getApplicationTypeLabel(application);
  const appName = getClientApplicationName(application) || "Application";
  const price = formatFeeAmount(
    fee,
    fee.currency ||
      application.professional_fee?.currency ||
      application.currency ||
      "AUD",
  );

  // Format: application name · cost · Education/Migration
  return `${appName} · ${price} · ${typeLabel}`;
}

function normalizeFeeObject(fee, fallback = {}) {
  if (fee && typeof fee === "object") {
    const cost = Number(
      fee.cost ?? fee.professional_cost ?? fee.department_fee ?? fee.amount ?? 0,
    );
    const baseCost = Number(
      fee.base_cost ??
        fee.baseCost ??
        fee.department_base_fee ??
        fee.department_fee_base_cost ??
        cost,
    );
    if (!(cost > 0 || baseCost > 0)) return null;

    return {
      cost: cost > 0 ? cost : baseCost,
      base_cost: baseCost > 0 ? baseCost : cost,
      gst_status:
        fee.gst_status ||
        fee.tax ||
        fee.department_fee_gst_status ||
        fee.department_gst_status ||
        fallback.gst_status ||
        "",
      gst_percentage: Number(
        fee.gst_percentage ??
          fee.department_fee_gst_percentage ??
          fallback.gst_percentage ??
          0,
      ),
      currency:
        fee.currency ||
        fee.department_fee_currency ||
        fallback.currency ||
        "",
    };
  }

  const amount = Number(fee);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  return {
    cost: amount,
    base_cost: amount,
    gst_status: fallback.gst_status || "notax",
    gst_percentage: Number(fallback.gst_percentage) || 0,
    currency: fallback.currency || "",
  };
}

export function resolveApplicationProfessionalFee(application = {}) {
  const nested = normalizeFeeObject(application.professional_fee, {
    currency: application.currency || "",
    gst_status: application.gst_status || application.tax || "",
    gst_percentage: application.gst_percentage,
  });
  if (nested) return nested;

  return normalizeFeeObject(
    {
      cost: application.professional_cost ?? application.cost,
      base_cost:
        application.base_cost ??
        application.professional_base_cost ??
        application.professional_cost ??
        application.cost,
      gst_status: application.gst_status || application.tax || "",
      gst_percentage: application.gst_percentage,
      currency: application.currency || "",
    },
  );
}

export function resolveApplicationDepartmentFee(application = {}) {
  if (!isMigrationClientApplication(application)) return null;

  const nested = normalizeFeeObject(application.department_fee, {
    currency:
      application.department_fee_currency || application.currency || "",
    gst_status:
      application.department_fee_gst_status ||
      application.department_gst_status ||
      "",
    gst_percentage: application.department_fee_gst_percentage,
  });
  if (nested) return nested;

  return normalizeFeeObject({
    cost: application.department_fee,
    base_cost:
      application.department_base_fee ??
      application.department_fee_base_cost ??
      application.department_base_cost ??
      application.department_fee,
    gst_status:
      application.department_fee_gst_status ||
      application.department_gst_status ||
      "",
    gst_percentage: application.department_fee_gst_percentage,
    currency:
      application.department_fee_currency || application.currency || "",
  });
}

function buildFeeLineItemFields(
  application = {},
  fee = {},
  feeType = "professional",
) {
  const taxType = normalizeTaxType(fee.gst_status) || "notax";
  const gstPercentage = Number(fee.gst_percentage) || 0;
  const unitPrice = resolveFeeUnitPrice(fee, taxType);
  // Keep dropdown + line description consistent: type · name · status · fee
  const description = buildApplicationFeeLabel(application, fee);

  return {
    description,
    unit_price: unitPrice > 0 ? String(unitPrice) : "",
    tax_type: taxType,
    tax_rate: resolveLineItemTaxRate(taxType, gstPercentage),
    application_id: getClientApplicationId(application),
    application_type: isMigrationClientApplication(application)
      ? "migration"
      : application.application_type || application.type || "education",
    fee_type: feeType,
    currency: fee.currency || application.currency || "",
  };
}

export function buildClientApplicationOptionLabel(application = {}) {
  const professionalFee = resolveApplicationProfessionalFee(application);
  if (professionalFee) {
    return buildApplicationFeeLabel(application, professionalFee);
  }

  const departmentFee = resolveApplicationDepartmentFee(application);
  if (departmentFee) {
    return buildApplicationFeeLabel(application, departmentFee);
  }

  return buildApplicationFeeLabel(application, {});
}

function buildFeeSuggestionLabel(application = {}, fee = {}) {
  return buildApplicationFeeLabel(application, fee);
}

/**
 * One suggestion per billable fee (professional and/or department),
 * so migration department fees appear as selectable options.
 */
export function buildClientApplicationFeeSuggestionOptions(
  applications = [],
  { financeInvoices = [] } = {},
) {
  const options = [];

  (Array.isArray(applications) ? applications : []).forEach((application) => {
    const appId = getClientApplicationId(application);
    if (appId == null || appId === "") return;

    const professionalFee = resolveApplicationProfessionalFee(application);
    if (professionalFee) {
      const invoicedAmount = resolveApplicationFeeInvoicedFromFinanceInvoices(
        financeInvoices,
        appId,
        "professional",
      );
      const remaining = resolveFeeRemainingAmount(professionalFee, invoicedAmount);
      if (remaining > 0.009) {
        const feeForLabel = {
          ...professionalFee,
          cost: remaining,
          base_cost: remaining,
        };
        options.push({
          id: `${appId}-professional`,
          label:
            invoicedAmount > 0.009
              ? `${buildFeeSuggestionLabel(application, feeForLabel)} · Remaining ${formatApplicationMoney(remaining)}`
              : buildFeeSuggestionLabel(application, feeForLabel),
          kind: "suggestion",
          fields: buildFeeLineItemFields(
            application,
            { ...professionalFee, cost: remaining, base_cost: remaining },
            "professional",
          ),
        });
      }
    }

    const departmentFee = resolveApplicationDepartmentFee(application);
    if (departmentFee) {
      const invoicedAmount = resolveApplicationFeeInvoicedFromFinanceInvoices(
        financeInvoices,
        appId,
        "department",
      );
      const remaining = resolveFeeRemainingAmount(departmentFee, invoicedAmount);
      if (remaining > 0.009) {
        const feeForLabel = {
          ...departmentFee,
          cost: remaining,
          base_cost: remaining,
        };
        options.push({
          id: `${appId}-department`,
          label:
            invoicedAmount > 0.009
              ? `${buildFeeSuggestionLabel(application, feeForLabel)} · Remaining ${formatApplicationMoney(remaining)}`
              : buildFeeSuggestionLabel(application, feeForLabel),
          kind: "suggestion",
          fields: buildFeeLineItemFields(
            application,
            { ...departmentFee, cost: remaining, base_cost: remaining },
            "department",
          ),
        });
      }
    }
  });

  return options;
}

export function buildLineItemsFromClientApplication(application = {}) {
  const items = [];
  const professionalFee = resolveApplicationProfessionalFee(application);
  const departmentFee = resolveApplicationDepartmentFee(application);

  if (professionalFee) {
    items.push(
      buildFeeLineItemFields(application, professionalFee, "professional"),
    );
  }

  if (departmentFee) {
    items.push(
      buildFeeLineItemFields(application, departmentFee, "department"),
    );
  }

  return items;
}

export function findClientApplicationById(applications = [], applicationId) {
  if (!applicationId) return null;

  return (
    applications.find(
      (application) =>
        String(getClientApplicationId(application)) === String(applicationId),
    ) || null
  );
}
